import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Bounty, BountyClaim, BountyInvite, BountyProposal } from '@foodfluence/shared';
import { InfluencerDataContext } from './InfluencerDataContext';
import { bountyApi } from '../services/api/bounties';

export type BountyDataContextValue = {
  bounties: Bounty[];
  proposals: BountyProposal[];
  claims: BountyClaim[];
  /** Admin-sent invites addressed to the signed-in creator. */
  invites: BountyInvite[];
  loading: boolean;
  /** True until the first bootstrap fetch completes (or is known to have nothing to load). */
  claimsLoading: boolean;
  error: string | null;
  refetchBounties: () => Promise<void>;
};

const defaultValue: BountyDataContextValue = {
  bounties: [], proposals: [], claims: [], invites: [], loading: true, claimsLoading: true, error: null,
  refetchBounties: async () => undefined,
};

export const BountyDataContext = createContext<BountyDataContextValue>(defaultValue);

export function useBountyData(): BountyDataContextValue {
  return useContext(BountyDataContext);
}

export const BountyDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const influencerId = influencerData?.id;
  const [bounties, setBounties] = useState<Bounty[]>([]);
  const [proposals, setProposals] = useState<BountyProposal[]>([]);
  const [claims, setClaims] = useState<BountyClaim[]>([]);
  const [invites, setInvites] = useState<BountyInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchBounties = useCallback(async () => {
    if (!influencerId) {
      setBounties([]);
      setProposals([]);
      setClaims([]);
      setInvites([]);
      setLoading(false);
      setClaimsLoading(false);
      return;
    }
    try {
      setError(null);
      const res = await bountyApi.getBounties(influencerId);
      setBounties(res.bounties ?? []);
      setProposals(res.proposals ?? []);
      setClaims(res.claims ?? []);
      setInvites(res.invites ?? []);
    } catch (e) {
      console.error('Error fetching bounty data', e);
      setError('Could not load bounties.');
    } finally {
      setLoading(false);
      setClaimsLoading(false);
    }
  }, [influencerId]);

  useEffect(() => {
    setLoading(true);
    setClaimsLoading(true);
    refetchBounties();
  }, [refetchBounties]);

  // Refresh when the creator returns to the tab — covers admin verify/reject while away.
  useEffect(() => {
    if (!influencerId) return;
    const onFocus = () => { refetchBounties(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [influencerId, refetchBounties]);

  return (
    <BountyDataContext.Provider value={{ bounties, proposals, claims, invites, loading, claimsLoading, error, refetchBounties }}>
      {children}
    </BountyDataContext.Provider>
  );
};

export default BountyDataProvider;
